import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { OrderStatusCount } from "../../../api/types/analytics.types";
import { chartTheme } from "../../../config/chartTheme";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import styles from "./Chart.module.scss";

interface StatusDistributionChartProps {
  data: OrderStatusCount[] | null | undefined;
}

const StatusDistributionChart = ({ data }: StatusDistributionChartProps) => {
  const { t } = useTranslation("translation");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

  const handleResize = useDebounce(() => {
    setIsMobile(window.innerWidth <= 576);
  }, 150);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      "1": t("order_status.incoming") || "Incoming",
      "2": t("order_status.packing") || "Packing",
      "3": t("order_status.ready_for_admin") || "Ready For Admin",
      "4": t("order_status.out_for_delivery") || "Out For Delivery",
      "5": t("order_status.delivered") || "Delivered",
      "6": t("order_status.pickup_tasks") || "Pickup Tasks",
    };
    return statusMap[status] || `Status ${status}`;
  };

  // Handle null/undefined data by defaulting to empty array
  const safeData = data || [];
  const chartData = safeData.map((item) => ({
    name: getStatusLabel(item.status),
    value: item.count,
    status: item.status,
  }));

  const COLORS = [
    chartTheme.colors.primary,
    chartTheme.colors.secondary,
    chartTheme.orderStatusColors["3"],
    chartTheme.orderStatusColors["4"],
    chartTheme.orderStatusColors["5"],
    chartTheme.orderStatusColors["6"],
  ];

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        {t("charts.status_distribution.title") || "Order Status Distribution"}
      </h3>
      <ResponsiveContainer
        width="100%"
        height={350}
        className={styles.chartWrapper}
      >
        <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => {
              const percentage =
                percent != null ? (percent * 100).toFixed(0) : "0";
              return isMobile ? `${percentage}%` : `${name}: ${percentage}%`;
            }}
            outerRadius={isMobile ? 70 : 100}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: chartTheme.colors.background,
              border: `1px solid ${chartTheme.colors.border}`,
              borderRadius: chartTheme.borderRadius.tooltip,
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 11 : chartTheme.font.sizes.base,
              padding: isMobile ? "8px" : undefined,
            }}
          />
          <Legend
            wrapperStyle={{
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 11 : chartTheme.font.sizes.base,
              color: chartTheme.colors.text,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StatusDistributionChart;
